/**
 * EsportsPage — E-스포츠 tab
 * Same two-panel layout as InternationalPage:
 *   lsp_page (flex row)
 *     lsp_shell (left 50%) → lsp_sheller (hero+tabs+search) + lsp_list (matches)
 *     lsp_detail (right flex:1) → breadcrumb + match header + markets
 *
 * Fixtures fetched with sport=esports.
 * Game-title tabs + sidebar clicks both set selectedGameTitleId → game_title_id param.
 */
import React, { useState, useEffect, useContext, useMemo } from 'react';
import './InternationalPage.css';
import { SportsBetCartContext } from '../../../_context/SportsBetCartContext';
import { SportsnewContext } from '../../../_context/SportsnewContext';
import useServiceContextHook from '../../../_hooks/service.context.hook';
import FigmaBetCart from './FigmaBetCart';
import { sortMarketRow, deriveGroupKey, oddRowLineLabel, groupMarketsByType } from '../_helpers/marketRowSorting';
import { buildSportsnewMarketTabs, ensureSportsnewMarketTab, marketMatchesSportsnewCategory } from '../_helpers/sportsnewMarketCategories';
import { isOddLocked, marketIdentityKey } from '../../../_context/_helpers/sportsnewListPatch';
import { formatSportsMatchTime, hasSportsMatchStartElapsed } from '../_helpers/sportsMatchTime';
import { mainOddsResultLabel } from '../_helpers/mainOddsPolicy';
import getSelectionLabel from './selectionLabelResolver';

import slide1 from '../../../assets/Slider/slide1.png';
import slide2 from '../../../assets/Slider/slide2.png';
import slide3 from '../../../assets/Slider/slide3.png';
import dressImage1 from '../../../assets/away_team_red.png';
import dressImage2 from '../../../assets/away_team_blue.png';

import { LspMatchListSkeleton, LspMarketDetailSkeleton } from '../../../components/Skeleton/Skeleton';
import {
  Search, ArrowDownUp, ArrowUpDown, RefreshCw,
  ChartNoAxesColumn, ReceiptText, Loader, Lock,
} from 'lucide-react';

const SLIDER_IMAGES = [slide1, slide2, slide3];

const fmtOdds = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n.toFixed(2) : '—'; };
const serverOrder = (match) => {
  const n = Number(match?.serverOrder);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};
const compareServerOrder = (a, b) =>
  (serverOrder(a) - serverOrder(b)) || String(a.id || '').localeCompare(String(b.id || ''));

const slotState = (slot, fixture, nowMs) => {
  if (!slot?.oddStateId) return 'MISSING';
  if (hasSportsMatchStartElapsed(fixture, nowMs)) return 'LOCKED';
  if (isOddLocked(slot)) return 'LOCKED';
  return 'AVAILABLE';
};

const packRows = (rows) => {
  const packed = [];
  let pending = null;
  for (const row of rows) {
    if (row.length === 1) {
      if (pending) { packed.push([pending[0], row[0]]); pending = null; }
      else { pending = row; }
    } else {
      if (pending) { packed.push(pending); pending = null; }
      packed.push(row);
    }
  }
  if (pending) packed.push(pending);
  return packed;
};
// Fixed Asia/Seoul formatting — never the browser's local timezone.
const fmtTime = (f) => formatSportsMatchTime(f);
const esportsCompetitionLabel = (fixture) => (
  fixture?.leagueNameKo
  || fixture?.leagueName
  || fixture?.esports?.compactLabelKo
  || fixture?.esports?.compactLabel
  || fixture?.league?.name
  || ''
);
const esportsGameTitleLabel = (fixture) => (
  fixture?.esports?.gameTitleNameKo
  || fixture?.esports?.gameTitleName
  || fixture?.gameTitleName
  || fixture?.gameTitle
  || ''
);
const esportsGameIconUrl = (item) => (
  item?.iconUrl
  || item?.icon
  || item?.gameTitleIconUrl
  || item?.gameIcon
  || item?.esports?.gameTitleIconUrl
  || item?.esports?.iconUrl
  || null
);
const EsportsGameIcon = ({ item, className = '', size = 18 }) => {
  const iconUrl = esportsGameIconUrl(item);
  if (iconUrl) {
    return (
      <img
        src={iconUrl}
        alt=""
        className={className}
        style={{ width: size, height: size, objectFit: 'contain' }}
        onError={(event) => { event.currentTarget.style.display = 'none'; }}
      />
    );
  }
  return (
    <span
      className={className}
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size,
        lineHeight: 1,
      }}
    >
      🎮
    </span>
  );
};

/* ════════════════════════════════════════════════════ */
const EsportsPage = () => {
  const { toggleSelection, isSelected } = useContext(SportsBetCartContext);
  const {
    fixtures, loading,
    setMode, setDisplayScope, setSelectedSport,
    selectedGameTitleId, setSelectedGameTitleId,
    setSelectedTournamentId,
    detailFixture, detailMarkets, detailLoading,
    loadFixtureDetail, clearFixtureDetail,
    oddsFlash,
    page,
    totalPages,
    goToPage,
    fetchFixtures,
  } = useContext(SportsnewContext);
  const { services: { SportsnewService } } = useServiceContextHook();

  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [detailOpen, setDetailOpen]         = useState(false);
  const [activeMatch, setActiveMatch]       = useState(null);
  const [activeMktTab, setActiveMktTab]     = useState('all');
  const [sortOrder, setSortOrder]           = useState('time');
  const [searchVal, setSearchVal]           = useState('');
  const [sliderIndex, setSliderIndex]       = useState(0);
  const [nowMs, setNowMs]                   = useState(() => Date.now());

  useEffect(() => {
    const timerId = window.setInterval(() => setNowMs(Date.now()), 10000);
    return () => window.clearInterval(timerId);
  }, []);
  const [gameTitles, setGameTitles]         = useState([]);

  /* ── Mount: sport=esports ── */
  useEffect(() => {
    setMode('prematch');
    setDisplayScope('esports');
    setSelectedSport('esports');
    return () => {
      setSelectedSport(null);
      setSelectedGameTitleId(null);
      setSelectedTournamentId(null);
    };
  }, [setMode, setDisplayScope, setSelectedSport, setSelectedGameTitleId, setSelectedTournamentId]);

  /* ── Banner autoplay ── */
  useEffect(() => {
    const id = setInterval(() => setSliderIndex((p) => (p + 1) % SLIDER_IMAGES.length), 3000);
    return () => clearInterval(id);
  }, []);

  /* ── Fetch game title list for top tabs ── */
  useEffect(() => {
    if (!SportsnewService) return;
    SportsnewService.getNavigation({ mode: 'prematch', sport: 'esports', scope: 'esports' })
      .then((res) => {
        const d = res?.data;
        const raw = d?.gameTitles ?? d?.games ?? d?.items ?? (Array.isArray(d) ? d : []);
        setGameTitles(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {});
  }, [SportsnewService]);

  /* ── Normalise + search filter ── */
  const normalisedFixtures = useMemo(() => {
    const q = searchVal.trim().toLowerCase();
    const dedupMap = new Map();
    for (const f of fixtures) {
      const raw = f.fixtureId ?? f.id;
      if (raw == null) continue;
      const key = String(raw);
      if (!dedupMap.has(key)) dedupMap.set(key, f);
    }
    return Array.from(dedupMap.values())
      .filter((f) => {
        // Drop non-esports fixtures — shared context may hold fixtures from other tabs
        const sport = (f.sport ?? f.sportSlug ?? f.sportKey ?? '').toLowerCase();
        if (sport && sport !== 'esports') return false;
        if (!q) return true;
        return (f.home ?? '').toLowerCase().includes(q)
          || (f.away ?? '').toLowerCase().includes(q)
          || (f.leagueName ?? f.league?.name ?? '').toLowerCase().includes(q);
      })
      .map((f, index) => {
        const mo = f.mainOdds ?? {};
        const hp = mo.home?.priceDecimal;
        const dp = mo.draw?.priceDecimal;
        const ap = mo.away?.priceDecimal;
        const competitionName = esportsCompetitionLabel(f);
        return {
          _raw: f,
          id: String(f.fixtureId ?? f.fixture_id ?? f.id ?? f.matchId ?? f.match_id),
          serverOrder: f.rowOrder ?? f.row_order ?? index,
          leagueName: competitionName,
          gameTitle: esportsGameTitleLabel(f),
          gameIcon: esportsGameIconUrl(f),
          time: fmtTime(f),
          plusN: f.marketCount ?? 0,
          home: f.home ?? '홈팀',
          away: f.away ?? '원정팀',
          homeLogo: f.homeLogo || null,
          awayLogo: f.awayLogo || null,
          homeOdds: hp != null ? fmtOdds(hp) : null,
          drawOdds: dp != null ? fmtOdds(dp) : null,
          awayOdds: ap != null ? fmtOdds(ap) : null,
          homeBettable: mo.home?.bettable ?? false,
          drawBettable: mo.draw?.bettable ?? false,
          awayBettable: mo.away?.bettable ?? false,
          homeState: slotState(mo.home, f, nowMs),
          drawState: mo.draw ? slotState(mo.draw, f, nowMs) : null,
          awayState: slotState(mo.away, f, nowMs),
          _homeOdd: mo.home?.oddStateId ? { ...mo.home, id: mo.home.oddStateId, oddStateId: mo.home.oddStateId, odds: hp, priceDecimal: hp, name: mo.home.label ?? 'Home', bettable: mo.home.bettable ?? true } : null,
          _drawOdd: mo.draw?.oddStateId ? { ...mo.draw, id: mo.draw.oddStateId, oddStateId: mo.draw.oddStateId, odds: dp, priceDecimal: dp, name: mo.draw.label ?? 'Draw', bettable: mo.draw.bettable ?? true } : null,
          _awayOdd: mo.away?.oddStateId ? { ...mo.away, id: mo.away.oddStateId, oddStateId: mo.away.oddStateId, odds: ap, priceDecimal: ap, name: mo.away.label ?? 'Away', bettable: mo.away.bettable ?? true } : null,
        };
      });
  }, [fixtures, searchVal, nowMs]);

  /* ── Sort ── */
  const sortedMatches = useMemo(() => [...normalisedFixtures].sort((a, b) =>
    sortOrder === 'time'
      ? (a.time || '').localeCompare(b.time || '') || compareServerOrder(a, b)
      : (a.leagueName || '').localeCompare(b.leagueName || '') || compareServerOrder(a, b)
  ), [normalisedFixtures, sortOrder]);

  /* ── Keep active match hydrated as WS list/fixture patches update the same id ── */
  useEffect(() => {
    setActiveMatch((prev) => {
      if (!sortedMatches.length) return null;
      if (!prev) return sortedMatches[0];
      return sortedMatches.find((m) => m.id === prev.id) ?? null;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedMatches]);

  /* ── Load markets when active match changes ── */
  useEffect(() => {
    if (!activeMatch?.id) { clearFixtureDetail(); return; }
    loadFixtureDetail(activeMatch.id);
  }, [activeMatch?.id, loadFixtureDetail, clearFixtureDetail]);

  /* ── Odds click ── */
  const handleOddsClick = (e, rawF, odd) => {
    e.stopPropagation();
    if (!odd?.bettable) return;
    toggleSelection({
      odds_id: odd.id,
      match_id: rawF.fixtureId ?? rawF.id,
      market_id: null,
      market_group_key: `${rawF.fixtureId ?? rawF.id}:mainOdds`,
      shortOddId: odd.shortOddId ?? null,
      canonicalSelectionKey: odd.canonicalSelectionKey ?? null,
      canonicalLineKey: odd.canonicalLineKey ?? null,
      canonicalPairKey: odd.canonicalPairKey ?? null,
      points: odd.points ?? odd.line ?? null,
      line: odd.line ?? odd.points ?? null,
      selectionLine: odd.selectionLine ?? odd.normalizedSelection ?? null,
      groupingKey: odd.groupingKey ?? odd.canonicalPairKey ?? null,
      odds: odd.odds,
      match_name: `${rawF.home ?? ''} vs ${rawF.away ?? ''}`,
      market_name: mainOddsResultLabel(rawF.mainOdds),
      selection_name_display: getSelectionLabel(odd, { match: rawF, fallback: odd.label ?? odd.name }),
    });
  };

  const renderListOddsBtn = (state, price, oddObj, raw, flashKey) => {
    if (state === 'MISSING') {
      return <button type="button" className="lsp_odds_value lsp_odds_missing" disabled>—</button>;
    }
    if (state === 'LOCKED') {
      return (
        <button type="button" className="lsp_odds_value lsp_odds_locked_state" disabled>
          <Lock size={10} strokeWidth={2.5} className="lsp_lock_icon" />
          <span>{price ?? '—'}</span>
        </button>
      );
    }
    return (
      <button
        type="button"
        className={`lsp_odds_value${isSelected(oddObj?.id) ? ' lsp_odds_value_active' : ''}${flashKey ? ` dp_odds_flash_${flashKey}` : ''}`}
        onClick={(e) => handleOddsClick(e, raw, oddObj)}
      >
        {price ?? '—'}
      </button>
    );
  };

  /* ── Filter + group detail markets — grouping keys off stable identity
     (marketFamily/periodScope/lineKey/pairKey), never mkt.name (see
     marketRowSorting.js) ── */
  const detailMarketSource = useMemo(() => (
    detailMarkets.length > 0 ? detailMarkets : (detailFixture?.markets ?? [])
  ), [detailMarkets, detailFixture]);

  const marketTabs = useMemo(() => buildSportsnewMarketTabs({
    fixture: detailFixture,
    markets: detailMarketSource,
    sport: 'esports',
  }), [detailFixture, detailMarketSource]);

  useEffect(() => {
    const next = ensureSportsnewMarketTab(marketTabs, activeMktTab);
    if (next !== activeMktTab) setActiveMktTab(next);
  }, [marketTabs, activeMktTab]);

  const groupedMarkets = useMemo(() => {
    const filtered = detailMarketSource.filter((mkt) =>
      marketMatchesSportsnewCategory(mkt, activeMktTab));
    // The API sends one array entry per LINE, sharing one type identity
    // (marketTypeKey / marketStateId) across e.g. Asian Total Goals'
    // 1.5/2.5/3.5 — merge those back into one logical market before
    // splitting into rows, so every line lands as a ROW under one shared
    // section instead of three separate repeated sections.
    return groupMarketsByType(filtered).map((mkt) => {
      const odds = mkt._mergedOdds ?? mkt.selections ?? mkt.odds ?? [];
      const rowMap = new Map();
      const rowOrder = [];
      odds.forEach((odd) => {
        const key = deriveGroupKey(odd, mkt);
        if (!rowMap.has(key)) { rowMap.set(key, []); rowOrder.push(key); }
        rowMap.get(key).push(odd);
      });
      const home = activeMatch?.home;
      const away = activeMatch?.away;
      const rows = packRows(rowOrder.map((k) => sortMarketRow(rowMap.get(k), home, away)));
      return {
        ...mkt,
        _rows: rows,
        // Market titles stay line-free; every row/selection already carries
        // its own line via oddRowLineLabel.
        _lineLabel: '',
      };
    });
  }, [detailMarketSource, activeMktTab, activeMatch?.home, activeMatch?.away]);

  /* ── Active game title label ── */
  const activeGT = useMemo(
    () => gameTitles.find((g) => String(g.gameTitleId ?? g.countryId) === String(selectedGameTitleId ?? '')),
    [gameTitles, selectedGameTitleId]
  );

  /* ══════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════ */
  return (
    <div className="lsp_page">

      {/* ═══════════════════════════════════════════
          LEFT — lsp_shell (width: 50%)
          contains: header area + scrollable match list
          ═══════════════════════════════════════════ */}
      <div className="lsp_shell">

        {/* ── Header area: hero + tabs + search + sort ── */}
        <div className="lsp_sheller">

          {/* Hero slider */}
          <div className="lsp_hero">
            <div className="lsp_hero_track" style={{ transform: `translateX(-${sliderIndex * 100}%)` }}>
              {SLIDER_IMAGES.map((img, idx) => (
                <img key={idx} src={img} alt="" className="lsp_hero_img" />
              ))}
            </div>
          </div>

          {/* Game title tabs */}
          <div className="lsp_sport_tabs lsp_esports_game_tabs" style={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
            <button
              type="button"
              className={`lsp_sport_tab lsp_esports_game_tab ${!selectedGameTitleId ? 'lsp_sport_tab_active' : ''}`}
              onClick={() => {
                setSelectedGameTitleId(null);
                setSelectedTournamentId(null);
              }}
            >
              <EsportsGameIcon item={null} className="lsp_esports_game_tab_icon" size={24} />
              <span>전체</span>
            </button>
            {gameTitles.map((g) => {
              const gtId = g.gameTitleId ?? g.countryId;
              return (
                <button
                  key={gtId}
                  type="button"
                  className={`lsp_sport_tab lsp_esports_game_tab ${String(selectedGameTitleId ?? '') === String(gtId ?? '') ? 'lsp_sport_tab_active' : ''}`}
                  onClick={() => {
                    setSelectedTournamentId(null);
                    setSelectedGameTitleId((prev) => String(prev ?? '') === String(gtId ?? '') ? null : gtId);
                  }}
                >
                  <EsportsGameIcon item={g} className="lsp_esports_game_tab_icon" size={24} />
                  <span>{g.displayName || g.nameKo || g.name}</span>
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="lsp_search_row">
            <span className="lsp_search_icon"><Search size={18} /></span>
            <input
              className="lsp_search_input"
              placeholder="팀명 또는 리그명을 입력해주세요."
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
            />
            <div className="lsp_search_actions">
              <button type="button" className="lsp_search_btn_icon">▶</button>
              <button type="button" className="lsp_search_btn_icon" onClick={() => setSearchVal('')}>✕</button>
            </div>
          </div>

          {/* Sort */}
          <div className="lsp_sort_row">
            <button type="button"
              className={`lsp_sort_btn ${sortOrder === 'time' ? 'lsp_sort_btn_active' : ''}`}
              onClick={() => setSortOrder('time')}
            >
              <ArrowDownUp size={14} /> 시간별정렬
            </button>
            <button type="button"
              className={`lsp_sort_btn ${sortOrder === 'league' ? 'lsp_sort_btn_active' : ''}`}
              onClick={() => setSortOrder('league')}
            >
              <ArrowUpDown size={14} /> 리그별정렬
            </button>
            <div className="lsp_sort_spacer" />
            <button type="button" className="lsp_sort_refresh" disabled={loading} onClick={() => fetchFixtures()}><RefreshCw size={14} /></button>
          </div>
        </div>{/* /lsp_sheller */}

        {/* ── Scrollable match list ── */}
        <div className="lsp_list">
          <div className="lsp_list_scroll">
            {loading && <LspMatchListSkeleton count={5} />}
            {!loading && sortedMatches.length === 0 && (
              <div className="lsp_empty">진행 중인 E-스포츠 경기가 없습니다.</div>
            )}
            {!loading && sortedMatches.map((m) => (
              <div key={m.id} className={`lsp_match_group ${activeMatch?.id === m.id ? 'lsp_match_group_active' : ''}`}>
                {/* League header */}
                <div className="lsp_league_header">
                  <EsportsGameIcon item={m} className="lsp_esports_league_icon" size={18} />
                  <span className="lsp_league_name">{m.leagueName}</span>
                  <span className="lsp_league_time">{m.time}</span>
                  <span className="lsp_league_stats">
                    <ChartNoAxesColumn size={15} style={{ color: 'var(--lsp-pink)' }} />
                  </span>
                  <button type="button" className="lsp_more_badge"
                    onClick={(e) => { e.stopPropagation(); setActiveMatch(m); setDetailOpen(true); }}
                  >
                    +{m.plusN}
                  </button>
                </div>

                {/* Match row */}
                <div
                  className={`lsp_match_row ${activeMatch?.id === m.id ? 'lsp_match_row_active' : ''}`}
                  onClick={() => { setActiveMatch(m); setDetailOpen(true); }}
                >
                  <div className="lsp_teams_col">
                    <div className="lsp_team_row">
                      <img src={m.homeLogo || dressImage1} className="lsp_team_jersey" alt="" />
                      <span className="lsp_team_name">{m.home}</span>
                    </div>
                    <div className="lsp_team_row">
                      <img src={m.awayLogo || dressImage2} className="lsp_team_jersey" alt="" />
                      <span className="lsp_team_name">{m.away}</span>
                    </div>
                  </div>

                  <div className="lsp_odds_cols">
                    {/* Home */}
                    <div className="lsp_odds_col">
                      <span className="lsp_odds_label lsp_odds_label_home">Home</span>
                      {renderListOddsBtn(m.homeState, m.homeOdds, m._homeOdd, m._raw, oddsFlash[m._homeOdd?.id])}
                    </div>

                    {/* Draw or vs separator */}
                    {m.drawState != null ? (
                      <div className="lsp_odds_col">
                        <span className="lsp_odds_label lsp_odds_label_draw">Draw</span>
                        {renderListOddsBtn(m.drawState, m.drawOdds, m._drawOdd, m._raw, null)}
                      </div>
                    ) : (
                      <div className="lsp_odds_col lsp_odds_col_vs">
                        <span className="lsp_vs_label">vs</span>
                      </div>
                    )}

                    {/* Away */}
                    <div className="lsp_odds_col">
                      <span className="lsp_odds_label lsp_odds_label_away">Away</span>
                      {renderListOddsBtn(m.awayState, m.awayOdds, m._awayOdd, m._raw, oddsFlash[m._awayOdd?.id])}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="lsp_pagination">
              <button
                className="lsp_page_btn"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
              >‹</button>
              <span className="lsp_page_info">{page} / {totalPages}</span>
              <button
                className="lsp_page_btn"
                disabled={page >= totalPages}
                onClick={() => goToPage(page + 1)}
              >›</button>
            </div>
          )}
        </div>{/* /lsp_list */}

      </div>{/* /lsp_shell */}

      {/* ═══════════════════════════════════════════
          RIGHT — lsp_detail (flex: 1)
          Sibling of lsp_shell inside lsp_page
          ═══════════════════════════════════════════ */}
      {activeMatch && (
        <div className={`lsp_detail ${detailOpen ? 'lsp_detail_mobile_open' : ''}`}>
          <button type="button" className="lsp_detail_close_btn" onClick={() => setDetailOpen(false)}>
            ✕ 닫기
          </button>

          {/* Breadcrumb */}
          <div className="lsp_detail_topnav">
            <div className="lsp_detail_sport_chip">
              <EsportsGameIcon item={activeGT || activeMatch} className="lsp_detail_chip_icon" size={16} />
              {activeGT?.displayName || activeGT?.nameKo || activeGT?.name || 'E-스포츠'}
            </div>
            <span className="lsp_detail_nav_sep">/</span>
            <div className="lsp_detail_league_chip">
              <EsportsGameIcon item={activeMatch} className="lsp_detail_chip_icon" size={16} />
              {activeMatch.leagueName}
            </div>
          </div>

          {/* Match header */}
          <div className="lsp_match_header">
            <div className="lsp_match_usersbox">
              <img width={30} src={activeMatch.homeLogo || dressImage1} alt="" />
              <span>{activeMatch.home}</span>
            </div>
            <div className="lsp_match_timebox">
              <span>{(activeMatch.time || '').split(' ')[0]}</span>
              <span> | </span>
              <span>{(activeMatch.time || '').split(' ')[1]}</span>
            </div>
            <div className="lsp_match_usersbox">
              <span>{activeMatch.away}</span>
              <img width={30} src={activeMatch.awayLogo || dressImage2} alt="" />
            </div>
          </div>

          {/* Game thumbnail */}
          {(activeMatch._raw?.thumbnail ?? activeMatch._raw?.image ?? activeGT?.thumbnail) && (
            <img
              src={activeMatch._raw?.thumbnail ?? activeMatch._raw?.image ?? activeGT?.thumbnail}
              alt=""
              style={{ width: '100%', maxHeight: 200, objectFit: 'cover' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}

          {/* Market sub-tabs */}
          <div className="lsp_mkt_tabs">
            {marketTabs.map((tab) => (
              <button key={tab.id} type="button"
                className={`lsp_mkt_tab ${activeMktTab === tab.id ? 'lsp_mkt_tab_active' : ''}`}
                onClick={() => setActiveMktTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Market sections */}
          <div className="lsp_detail_scroll">
            {detailLoading && <LspMarketDetailSkeleton count={5} />}

            {!detailLoading && groupedMarkets.map((mkt) => {
              if (!mkt.bettable && mkt.suspended) return null;
              const mktId = mkt.marketStateId ?? mkt.id ?? mkt.name;
              // A single marketStateId can be reused across multiple DISTINCT
              // markets (different lines/periods/team-scoped variants) — the
              // section key (and the betslip's market_group_key below) must
              // use the fuller identity (marketGroupId first, else a
              // composite fallback), or two such markets collide and only
              // one renders/selects.
              const groupKey = marketIdentityKey(mkt) ?? mktId;
              return (
                <div key={groupKey} className="lsp_mkt_section">
                  <div className="lsp_mkt_section_header">{mkt.name}{mkt._lineLabel}</div>
                  {mkt._rows.map((row) => {
                    // A merged multi-line section holds several distinct
                    // lines under one groupKey now — the betslip's
                    // exclusivity key must stay scoped to THIS row's own
                    // line, or e.g. "Over 2.5" and "Over 1.5" would wrongly
                    // collide as if they were the same market.
                    const rowGroupKey = `${groupKey}:${deriveGroupKey(row[0], mkt)}`;
                    return (
                    <div key={rowGroupKey} className={row.length === 3 ? 'lsp_grid_row_3' : 'lsp_grid_row'}>
                      {row.map((odd) => {
                        const isLocked  = isOddLocked(odd);
                        const isMissing = !isLocked && (!odd.oddStateId || odd.priceDecimal == null);
                        const cellState = isMissing ? 'MISSING' : isLocked ? 'LOCKED' : 'AVAILABLE';
                        const oddLabel = getSelectionLabel(odd, { market: mkt, match: activeMatch });
                        const oddKey = odd.oddStateId ?? `${rowGroupKey}:${odd.name ?? oddLabel}`;
                        return (
                        <button
                          key={oddKey}
                          type="button"
                          className={`lsp_grid_cell${isSelected(odd.oddStateId) ? ' lsp_grid_cell_active' : ''}${cellState === 'LOCKED' ? ' dp_cell_locked' : ''}${cellState === 'MISSING' ? ' dp_cell_missing' : ''}${oddsFlash[odd.oddStateId] ? ` dp_odds_flash_${oddsFlash[odd.oddStateId]}` : ''}`}
                          disabled={cellState !== 'AVAILABLE'}
                          onClick={() => {
                            if (cellState !== 'AVAILABLE') return;
                            toggleSelection({
                              odds_id: odd.oddStateId,
                              match_id: activeMatch.id,
                              market_id: mktId,
                              market_group_key: `${activeMatch.id}:${rowGroupKey}`,
                              shortOddId: odd.shortOddId ?? null,
                              canonicalSelectionKey: odd.canonicalSelectionKey ?? null,
                              canonicalLineKey: odd.canonicalLineKey ?? null,
                              canonicalPairKey: odd.canonicalPairKey ?? null,
                              points: odd.points ?? odd.line ?? null,
                              line: odd.line ?? odd.points ?? null,
                              selectionLine: odd.selectionLine ?? odd.normalizedSelection ?? null,
                              groupingKey: odd.groupingKey ?? odd.canonicalPairKey ?? null,
                              odds: odd.priceDecimal,
                              match_name: `${activeMatch.home} vs ${activeMatch.away}`,
                              market_name: mkt.name,
                              selection_name_display: oddLabel,
                            });
                          }}
                        >
                          <span className="lsp_grid_cell_label">{oddLabel}{oddRowLineLabel(odd, mkt)}</span>
                          {cellState === 'LOCKED'
                            ? <span className="dp_cell_locked_odds"><Lock size={10} strokeWidth={2.5} />{fmtOdds(odd.priceDecimal)}</span>
                            : <span className="lsp_grid_cell_odds">{cellState === 'MISSING' ? '—' : fmtOdds(odd.priceDecimal)}</span>
                          }
                        </button>
                        );
                      })}
                    </div>
                    );
                  })}
                </div>
              );
            })}

            {!detailLoading && groupedMarkets.length === 0 && (
              <div className="lsp_no_markets">배당 정보가 없습니다.</div>
            )}
          </div>
        </div>
      )}{/* /lsp_detail */}

      {/* Mobile float button */}
      <button type="button" className="ipp_mobile_cart_fab" onClick={() => setMobileCartOpen((p) => !p)}>
        <span className="ipp_mobile_cart_icon_wrap"><ReceiptText size={18} /></span>
        <span>베팅카트</span>
      </button>

      {detailOpen && <div className="lsp_scrim" onClick={() => setDetailOpen(false)} />}

      <FigmaBetCart isMobile mobileOpen={mobileCartOpen} onMobileClose={() => setMobileCartOpen(false)} />
    </div>
  );
};

export default EsportsPage;
